import React, { useCallback, useRef, useState } from "react";
import { Outlet } from "react-router-dom";

import { Menu, MenuOpen } from "@mui/icons-material";
import { AppBar, Box, Drawer, IconButton, Stack, Toolbar } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { HelpBox } from "@/components/atoms";
import { NavigationMenu, NotificationButton, UserMenu } from "@/components/molecules";
import { NavigationContext } from "@/context";
import { getDrawerTransition, useLogo } from "@/utils";

const BaseLayout: React.FC = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const logoConfig = useLogo();

  const navigation = React.useContext(NavigationContext);

  const [isMenuExpanded, setIsMenuExpanded] = useState(true);

  const toggleMenuExpanded = useCallback(() => {
    setIsMenuExpanded(!isMenuExpanded);
  }, [isMenuExpanded, setIsMenuExpanded]);

  const handleNavigationSelect = useCallback(() => {
    selectedItemIdRef.current = "";
  }, []);

  const getMenuIcon = useCallback(
    (isExpanded: boolean) => {
      return (
        <HelpBox title={isExpanded ? "Collapse Menu" : "Expand Menu"}>
          <IconButton onClick={toggleMenuExpanded} sx={{ borderRadius: "6px" }}>
            {isExpanded ? <MenuOpen /> : <Menu />}
          </IconButton>
        </HelpBox>
      );
    },
    [toggleMenuExpanded]
  );

  const getDrawerStyle = useCallback(() => {
    const drawerWidth = isMenuExpanded ? 260 : 64;

    return {
      display: "block",
      flexShrink: 0,
      width: drawerWidth,
      ...getDrawerTransition(isMenuExpanded, "width"),
      overflowX: "hidden",
      [`& .MuiDrawer-paper`]: {
        position: "absolute",
        width: drawerWidth,
        boxSizing: "border-box",
        ...getDrawerTransition(isMenuExpanded, "width"),
        overflowX: "hidden",
      },
    };
  }, [isMenuExpanded]);

  const layoutRef = useRef<Element | null>(null);
  const selectedItemIdRef = useRef("");

  return (
    <Box
      ref={layoutRef}
      sx={{
        position: "relative",
        display: "flex",
        overflow: "hidden",
        height: "100vh",
        width: "100vw",
        minWidth: "1028px",
        // minWidth: isMenuExpanded
        //   ? "calc(1028px - 240px)"
        //   : "calc(1028px - 64px)",
      }}
    >
      <AppBar
        color="inherit"
        position="absolute"
        sx={{ zIndex: theme.zIndex.drawer + 1 }}
      >
        <Toolbar variant="dense" sx={{ mx: -1 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              flexWrap: "wrap",
              width: "100%",
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              {navigation && getMenuIcon(isMenuExpanded)}
              {logoConfig &&
                (mode === "dark" ? (
                  <Box
                    sx={{
                      flexGrow: 0,
                      width: logoConfig.main.width,
                      height: logoConfig.main.height,
                      mr: 2,
                    }}
                  >
                    {<logoConfig.main.logo />}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      flexGrow: 0,
                      width: logoConfig.dark.width,
                      height: logoConfig.dark.height,
                      mr: 2,
                    }}
                  >
                    {<logoConfig.dark.logo />}
                  </Box>
                ))}
              {logoConfig && logoConfig.alt && (
                <Box
                  sx={{
                    flexGrow: 0,
                    width: logoConfig.alt.width,
                    height: logoConfig.alt.height,
                    mr: 2,
                  }}
                >
                  {<logoConfig.alt.logo />}
                </Box>
              )}
            </Stack>
            <NotificationButton />
          </Stack>
        </Toolbar>
      </AppBar>
      {navigation && (
        <Drawer
          variant="permanent"
          PaperProps={{ elevation: 2 }}
          sx={{
            ...getDrawerStyle(),
          }}
        >
          <Toolbar variant="dense" />
          <Box
            component="nav"
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              overflow: "auto",
              pt: navigation[0]?.kind === "header" && isMenuExpanded ? 0 : 2,
              ...getDrawerTransition(isMenuExpanded, "padding"),
            }}
          >
            <NavigationMenu
              navigation={navigation}
              isExpanded={isMenuExpanded}
              onSelect={handleNavigationSelect}
              selectedItemId={selectedItemIdRef.current}
            />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{
                borderTop: 1,
                borderColor: "divider",
                p: 1,
              }}
            >
              <UserMenu isMenuExpanded={isMenuExpanded} />
            </Stack>
          </Box>
        </Drawer>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        <Toolbar variant="dense" sx={{ displayPrint: "none" }} />
        <Box
          component="main"
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            overflow: "hidden",
            width: "100%",
            height: "100vh",
            maxWidth: isMenuExpanded
              ? "calc(100dvw - 260px)"
              : "calc(100dvw - 64px)",
            minWidth: isMenuExpanded
              ? "calc(1028px - 260px)"
              : "calc(1028px - 64px)",
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default BaseLayout;
